import { map, flatMap, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { DataService, ApiService, InspectionPluginService, UpgradesService } from '../../shared/services/index';
import { InspectionPlugin } from '../../shared/models/index';
import { InspectionFactoryService } from '../../shared/services/inspectionFactory.service';
import { ModalComponent } from '../../modal/modal.component';
import { UpgradeDetailsModalComponent } from '../upgrades/upgrade-details/upgrade-details.component';
import { UpgradeDetailsConfig } from '../upgrades/upgrades-plugin.service';
import { ConfigService } from '../../shared/services/config.service';

@Injectable({
  providedIn: 'root'
})
export class ObsolescencesPluginService extends InspectionPluginService {
  obsolescencesModal: ModalComponent;
  detailsConfig: UpgradeDetailsConfig[];
  constructor(
    protected apiService: ApiService,
    protected dataService: DataService,
    factory: InspectionFactoryService,
    private configService: ConfigService,
    private upgradesService: UpgradesService
  ) {
    super(apiService, dataService, null, factory);
  }

  updateObsolescences(
    inspectionId: string,
    pluginId: string,
    obsolescences: any,
    headers: any = {},
    resetToDraft = true
  ): Observable<any> {
    return this._update(inspectionId, pluginId, obsolescences, headers, resetToDraft).pipe(
      map((res: InspectionPlugin) => {
        return this.dataService.updatePlugin(inspectionId, res, resetToDraft);
      })
    );
  }

  getObsolescences(inspectionId: string, headers: { [header: string]: string | string[] } = {}) {
    if (this.isInspectionInOffline(inspectionId)) {
      return of([]);
    }
    return this.upgradesService.getUpgrades(inspectionId, headers).pipe(
      map((data) => {
        const obsolescences: any[] = data.map((d) => ({ ...d })).filter((u) => u.type === 'Obsolescence');
        const set = new Set();
        const uniqueObsolescences = obsolescences.filter((o) => {
          const duplicate = set.has(o.name);
          set.add(o.name);
          return !duplicate;
        });
        uniqueObsolescences.forEach((o) => {
          o.Name = o.name;
          o.Status = o.equiped = o.equiped?.toLowerCase()?.replace(' ', '-');
          o.Urgency = o.downTimeImpact = o.downTimeImpact?.toLowerCase();
          o.type = o.type?.toLowerCase();
        });
        return uniqueObsolescences;
      })
    );
  }

  setObsolescencesModal(obsolescencesModal: ModalComponent) {
    this.obsolescencesModal = obsolescencesModal;
    this.obsolescencesModal.hasClassModal = true;
    this.obsolescencesModal.modalFooter = false;
  }

  openObsolescencesDetails(upgrade: any, config: UpgradeDetailsConfig[]) {
    this.obsolescencesModal.open(UpgradeDetailsModalComponent, { upgrade, config });
  }

  getObsolescencesDetailsConfig(inspectionId): Observable<UpgradeDetailsConfig[]> {
    const key = 'plugins/upgrades/details-modal.json';
    if (this.detailsConfig) {
      return of(this.detailsConfig);
    }
    if (this.isInspectionInOffline(inspectionId) || !this.isConnection) {
      return of(JSON.parse(localStorage.getItem(key)));
    }
    return this.configService.getResource(key).pipe(
      tap((data) => {
        localStorage.setItem(key, JSON.stringify(data));
        this.detailsConfig = data;
      })
    );
  }

  private _update(
    inspectionId: string,
    pluginId: string,
    obsolescences: any,
    headers: any = {},
    resetToDraft = true
  ): Observable<any> {
    return this.factory.updatePluginConfig(inspectionId, null, pluginId, obsolescences, 'Obsolescences').pipe(
      flatMap((plugin) => {
        if (this.isInspectionInOffline(inspectionId)) {
          return of(plugin);
        }
        return this.apiService.post(`${plugin.postServerUrl}?resetToDraft=${resetToDraft}`, plugin.config, headers);
      })
    );
  }
}
